import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { leaveConference } from '../../base/conference/actions';
import Icon from '../../base/icons/components/Icon';
import { IconHangup, IconMic, IconMicSlash, IconScreenshare, IconVideo, IconVideoOff } from '../../base/icons/svg';
import { MEDIA_TYPE } from '../../base/media/constants';
import { getLocalParticipant } from '../../base/participants/functions';
import { isLocalTrackMuted } from '../../base/tracks/functions.any';
import { isPrejoinPageVisible } from '../../prejoin/functions.any';
import { isScreenVideoShared } from '../../screen-share/functions';
import { isDesktopShareButtonDisabled } from '../../toolbox/functions.web';
import { toggleAudioFromPiP, toggleScreenShareFromPiP, toggleVideoFromPiP } from '../actions';
import { getDocumentPiPWindow, getPiPGridParticipants } from '../functions';
import PiPTile from './PiPTile';
const useStyles = makeStyles()(() => {
    return {
        container: {
            background: '#000',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
            width: '100%'
        },
        grid: {
            display: 'grid',
            flex: 1,
            gap: '6px',
            gridAutoRows: '1fr',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            overflow: 'hidden',
            padding: '6px'
        },
        controls: {
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            padding: '8px'
        },
        button: {
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: '50%',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            fill: '#fff',
            height: '40px',
            justifyContent: 'center',
            width: '40px'
        },
        hangup: {
            background: '#d32f2f'
        },
        hangupWrapper: {
            position: 'relative'
        }
    };
});
// The hangup confirmation popover only renders after the user's first click,
// which is normally well after the Document PiP window's stylesheets were
// copied over (see openDocumentPiP / copyStylesToPiP in ../functions) — a
// CSS-in-JS class used for the first time at that point never makes it into
// the PiP window's stylesheet. Plain inline styles sidestep that entirely,
// same as the audio/video/screenshare toggle styles below.
const hangupConfirmStyle = {
    background: '#292929',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
    bottom: '48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    left: '50%',
    padding: '10px',
    position: 'absolute',
    transform: 'translateX(-50%)',
    width: '160px'
};
const hangupConfirmTextStyle = {
    color: '#fff',
    fontSize: '12px',
    textAlign: 'center'
};
const hangupConfirmActionsStyle = {
    display: 'flex',
    gap: '8px'
};
const hangupConfirmButtonBaseStyle = {
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    flex: 1,
    fontSize: '12px',
    padding: '6px 0'
};
const hangupConfirmCancelStyle = {
    ...hangupConfirmButtonBaseStyle,
    background: '#5e5e5e',
    color: '#fff'
};
const hangupConfirmSubmitStyle = {
    ...hangupConfirmButtonBaseStyle,
    background: '#d32f2f',
    color: '#fff'
};
/**
 * Renders an adaptive grid of participant tiles, the local self thumbnail and
 * call controls inside the always-on-top Document Picture-in-Picture window.
 *
 * @returns {React.ReactPortal | null}
 */
const DocumentPiP = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [pipWindow, setPipWindow] = useState(getDocumentPiPWindow());
    const [showHangupConfirm, setShowHangupConfirm] = useState(false);
    const isOnPrejoin = useSelector(isPrejoinPageVisible);
    const localParticipant = useSelector(getLocalParticipant);
    const gridParticipants = useSelector(getPiPGridParticipants);
    const audioMuted = useSelector((state) => isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO));
    const videoMuted = useSelector((state) => isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO));
    const screenSharing = useSelector(isScreenVideoShared);
    const screenShareDisabled = useSelector(isDesktopShareButtonDisabled);
    const onToggleAudio = useCallback(() => dispatch(toggleAudioFromPiP()), [dispatch]);
    const onToggleVideo = useCallback(() => dispatch(toggleVideoFromPiP()), [dispatch]);
    const onToggleScreenShare = useCallback(() => dispatch(toggleScreenShareFromPiP()), [dispatch]);
    const onHangupClick = useCallback(() => setShowHangupConfirm(value => !value), []);
    const onHangupCancel = useCallback(() => setShowHangupConfirm(false), []);
    const onHangupConfirm = useCallback(() => dispatch(leaveConference()), [dispatch]);
    // Toggled/disabled state must be applied via inline styles: dynamically
    // injected CSS-in-JS classes are not propagated into the Document PiP window
    // (its stylesheets are only synced shortly after it opens).
    // Muted audio/video get a grey circle, mirroring the room toolbar's
    // toggled state.
    const audioStyle = { background: audioMuted ? '#5e5e5e' : 'transparent' };
    const videoStyle = { background: videoMuted ? '#5e5e5e' : 'transparent' };
    const screenShareStyle = {
        // Buttons are transparent on the dark bar, mirroring the room toolbar.
        // Active: grey circle, matching the room toolbar's toggled state. The
        // green icon colour is set on the Icon via the `color` prop so it is
        // always green regardless of the default white SVG fill.
        background: screenSharing ? '#5e5e5e' : 'transparent',
        cursor: screenShareDisabled ? 'default' : 'pointer',
        opacity: screenShareDisabled ? 0.5 : 1
    };
    // Pick up the window opened by the togglePiP action.
    useEffect(() => {
        const win = getDocumentPiPWindow();
        setPipWindow(win);
        if (!win) {
            setShowHangupConfirm(false);
        }
    });
    if (!pipWindow) {
        return null;
    }
    return createPortal(React.createElement("div", { className: classes.container },
        React.createElement("div", { className: classes.grid },
            gridParticipants.map(p => (React.createElement(PiPTile, { key: p.id, participant: p }))),
            !isOnPrejoin && localParticipant && (React.createElement(PiPTile, { key: localParticipant.id, local: true, participant: localParticipant }))),
        React.createElement("div", { className: classes.controls },
            React.createElement("button", { className: classes.button, onClick: onToggleAudio, style: audioStyle },
                React.createElement(Icon, { size: 20, src: audioMuted ? IconMicSlash : IconMic })),
            React.createElement("button", { className: classes.button, onClick: onToggleVideo, style: videoStyle },
                React.createElement(Icon, { size: 20, src: videoMuted ? IconVideoOff : IconVideo })),
            React.createElement("button", { className: classes.button, disabled: screenShareDisabled, onClick: onToggleScreenShare, style: screenShareStyle },
                React.createElement(Icon, { color: 'rgb(0, 255, 0)', size: 20, src: IconScreenshare })),
            React.createElement("div", { className: classes.hangupWrapper },
                showHangupConfirm && (React.createElement("div", { style: hangupConfirmStyle },
                    React.createElement("span", { style: hangupConfirmTextStyle }, t('dialog.pipLeaveMeetingConfirm')),
                    React.createElement("div", { style: hangupConfirmActionsStyle },
                        React.createElement("button", { onClick: onHangupCancel, style: hangupConfirmCancelStyle }, t('dialog.cancel')),
                        React.createElement("button", { onClick: onHangupConfirm, style: hangupConfirmSubmitStyle }, t('dialog.confirm'))))),
                React.createElement("button", { className: cx(classes.button, classes.hangup), onClick: onHangupClick },
                    React.createElement(Icon, { size: 20, src: IconHangup }))))), pipWindow.document.body);
};
export default DocumentPiP;
